import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PrizeZoneCanvas } from './PrizeZoneCanvas';
import {
  type PrintArea, type MockupView, type MockupViewName, type DecorationRegistry,
  VALID_VIEWS, isTextCapable,
  PRIZE_FONTS, DEFAULT_PRIZE_FONT, DEFAULT_PRIZE_INK, resolvePrizeFont, resolvePrizeInk, isPrizeFont,
} from '@/lib/prize-product';
import {
  type Box, type Nat, type ImageLoadState, type ViewImageStates,
  VIEW_LABELS, addZone, addZoneDisabledReason, addableLocations, clampBox, codesBlockingViewRemoval,
  conflictNote, defaultBoxForZone, groupAreasByView, locationOptionLabel, overlappingZones,
  removeZone, setViewImage, setZoneBox, setZoneField, setZoneInches, setZoneView, unplacedCodes,
  zoneColor, zonesMissingImage, zonesWithBrokenImage,
} from '@/lib/prize-zones';

// The visual, multi-view print-zone editor for a prize product.
//
// One tab per garment side (Front / Back / Left Sleeve / Right Sleeve). Each
// tab owns ONE mockup image (mockupViews[view].baseImage — a filename in the
// ghostfit-product-mockups bucket) and every print area whose mockup.view
// points at it. Boxes are dragged on the image; print SIZE is typed in inches
// and is deliberately independent of the box (the box is where the art sits in
// the mockup, the inches are what the decorator prints).
type Props = {
  registry?: DecorationRegistry;
  productId: string;
  printAreas: Record<string, PrintArea>;
  mockupViews: Record<string, MockupView>;
  /** Filenames already in the mockup bucket (GET /api/prize-products/mockups). */
  mockupFiles: string[];
  /** Seed for a new zone's print size — the product's legacy production size. */
  defaultInches: { width: number; height: number };
  activeView: MockupViewName;
  onActiveViewChange: (v: MockupViewName) => void;
  onChange: (patch: { printAreas?: Record<string, PrintArea>; mockupViews?: Record<string, MockupView> }) => void;
  /** Re-read the bucket listing after an upload. */
  onUploaded: () => void;
  /**
   * Zones whose view image FAILED to load. The dialog blocks Save on this: a
   * filename that does not resolve here will not resolve for the proof renderer
   * either, and zonesMissingImage cannot see it because the string is present.
   */
  onBrokenImages?: (broken: { code: string; view: MockupViewName }[]) => void;
};

const inputCls = 'w-full rounded-[8px] border border-black/[0.08] bg-surface px-2 py-1.5 text-[12px] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/15';
const labelCls = 'block text-[10px] uppercase tracking-[0.04em] text-muted';

const num = (raw: string): number | undefined => {
  if (raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

export function PrizeZoneEditor({
  registry, productId, printAreas, mockupViews, mockupFiles, defaultInches,
  activeView, onActiveViewChange, onChange, onUploaded, onBrokenImages,
}: Props) {
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  // Natural image size per view, learned when the <img> loads. Needed so a new
  // zone gets a default box in the RIGHT pixel space.
  const [nats, setNats] = useState<Partial<Record<MockupViewName, Nat>>>({});
  const forgetNat = (v: MockupViewName) => setNats((p) => { const c = { ...p }; delete c[v]; return c; });
  // Whether each view's base image actually loaded. 'failed' is a DIFFERENT
  // state from 'loading' and needs a different action from the operator.
  const [imageStates, setImageStates] = useState<ViewImageStates>({});
  // The last zone deleted, so a misclick is one click to undo. Removal used to
  // mutate the draft with Cancel — losing every other edit — as the only way back.
  const [lastRemoved, setLastRemoved] = useState<{ code: string; area: PrintArea } | null>(null);

  const groups = useMemo(() => groupAreasByView(printAreas), [printAreas]);
  const allCodes = useMemo(() => Object.keys(printAreas), [printAreas]);
  const missing = useMemo(() => zonesMissingImage(printAreas, mockupViews), [printAreas, mockupViews]);
  // Only NON-conflicting overlaps. FF sitting over LC/RC is how every top is
  // built and must never be flagged — they can't share a submission.
  const overlaps = useMemo(() => overlappingZones(printAreas, registry), [printAreas, registry]);
  const unplaced = useMemo(() => unplacedCodes(printAreas), [printAreas]);
  const addable = useMemo(
    () => addableLocations(registry, activeView, printAreas),
    [registry, activeView, printAreas],
  );

  const view = mockupViews[activeView];
  const baseImage = view?.baseImage || '';
  const codes = groups[activeView];
  const blockingRemoval = codesBlockingViewRemoval(printAreas, activeView);
  // The MEASURED natural size of this view's image, or null until <img> onLoad
  // reports it. Null is not "use a default" — every control that would write
  // geometry is gated on it (addZoneDisabledReason / placeUnplaced), because a
  // box laid out against a guessed canvas saves as a real coordinate.
  const nat = nats[activeView] ?? null;
  const zoneAddBlocked = addZoneDisabledReason({
    registry, view: activeView, hasImage: !!baseImage, nat, addableCount: addable.length,
    imageState: imageStates[activeView],
  });

  // Zones pointing at a filename that does not load. Reported upward every time
  // it changes so the dialog's Save gate sees it.
  const broken = useMemo(
    () => zonesWithBrokenImage(printAreas, mockupViews, imageStates),
    [printAreas, mockupViews, imageStates],
  );
  // Keyed on the VALUE, not the callback identity: a parent passing an inline
  // arrow would otherwise re-fire this every render and ping-pong with its own
  // setState.
  const notifyBroken = useRef(onBrokenImages);
  notifyBroken.current = onBrokenImages;
  const brokenKey = broken.map((b) => `${b.code}@${b.view}`).join(',');
  useEffect(() => { notifyBroken.current?.(broken); }, [brokenKey]); // eslint-disable-line react-hooks/exhaustive-deps

  function noteImageState(view: MockupViewName, state: ImageLoadState) {
    setImageStates((p) => (p[view] === state ? p : { ...p, [view]: state }));
    // A failed image has no measurable size — drop any stale one so nothing can
    // be laid out against the dimensions of the image this one replaced.
    if (state !== 'loaded') forgetNat(view);
  }

  const setAreas = (next: Record<string, PrintArea>) => onChange({ printAreas: next });
  const setViews = (next: Record<string, MockupView>) => onChange({ mockupViews: next });

  async function uploadForView(file: File) {
    if (!productId) { toast.error('Set the product ID first — the file name is derived from it.'); return; }
    setUploading(true);
    try {
      const dims = await new Promise<{ w: number; h: number } | null>((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve({ w: img.naturalWidth, h: img.naturalHeight }); };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
        img.src = url;
      });
      if (!dims) { toast.error('Could not read that image.'); return; }
      if (dims.w < 500 || dims.h < 500) {
        toast.error(`Mockup too small (${dims.w}×${dims.h}px). Use at least 500×500.`);
        return;
      }
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(
        `/api/prize-products/${encodeURIComponent(productId)}/mockups/${encodeURIComponent(activeView)}`,
        { method: 'POST', credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: fd },
      );
      const body = await res.json().catch(() => ({} as { filename?: string; message?: string; error?: string }));
      if (!res.ok || !body.filename) {
        toast.error(body.message || body.error || `Upload failed (${res.status}).`);
        return;
      }
      onUploaded();
      // A replacement image can have a different natural size — forget the old
      // one so the next load re-measures instead of placing against stale dims.
      forgetNat(activeView);
      setViews(setViewImage(mockupViews, activeView, { baseImage: body.filename }));
      toast.success(`${VIEW_LABELS[activeView]} image uploaded`);
    } catch {
      toast.error('Upload failed.');
    } finally {
      setUploading(false);
    }
  }

  function removeViewImage() {
    if (blockingRemoval.length) {
      toast.error(`Move or delete ${blockingRemoval.join(', ')} first — ${VIEW_LABELS[activeView]} zones cannot proof without an image.`);
      return;
    }
    setViews(setViewImage(mockupViews, activeView, { baseImage: '' }));
  }

  // Removing a zone throws away a print size, a box and any text settings. It
  // used to be irreversible short of cancelling the whole dialog, which also
  // discards every unrelated edit — so the delete is remembered and offered back.
  function removeWithUndo(code: string) {
    const area = printAreas[code];
    if (!area) return;
    setLastRemoved({ code, area });
    setAreas(removeZone(printAreas, code));
    if (selectedCode === code) setSelectedCode(null);
  }

  function undoRemove() {
    if (!lastRemoved) return;
    const { code, area } = lastRemoved;
    if (printAreas[code]) { setLastRemoved(null); return; }  // re-added by hand
    setAreas({ ...printAreas, [code]: area });
    setSelectedCode(code);
    setLastRemoved(null);
  }

  // Type an exact coordinate. The third placement path alongside dragging and
  // arrow keys — and the only one that is precise by construction.
  function setBoxField(code: string, key: keyof Box, raw: string) {
    const area = printAreas[code];
    const m = area?.mockup;
    if (!m) return;
    const n = num(raw);
    if (n === undefined) return;
    const next: Box = { x: m.x, y: m.y, maxW: m.maxW, maxH: m.maxH, [key]: Math.round(n) };
    // Clamped against the measured image when we have one, so a typed number
    // cannot put a zone off the garment where a drag could not.
    setAreas(setZoneBox(printAreas, code, nat ? clampBox(next, nat) : next));
  }

  function placeUnplaced(code: string) {
    const area = printAreas[code];
    // Same rule as the Add-zone gate: no measured image, no coordinates. Says
    // why rather than doing nothing — this button is the one place an operator
    // can produce geometry without the picker.
    if (!nat) {
      toast.error(
        `Can't place ${code} yet — ${VIEW_LABELS[activeView]} has no loaded image to measure, `
        + "and a zone's position is stored in that image's pixels.",
      );
      return;
    }
    const box = defaultBoxForZone(nat, area.widthInches ?? 1, area.heightInches ?? 1, codes.length);
    setAreas(setZoneBox(setZoneView(printAreas, code, activeView), code, box));
    setSelectedCode(code);
  }

  return (
    <div className="space-y-3">
      {/* ---- view tabs ---- */}
      <div className="flex flex-wrap items-center gap-1.5">
        {VALID_VIEWS.map((v) => {
          const count = groups[v].length;
          const hasImage = !!mockupViews[v]?.baseImage;
          return (
            <button
              key={v}
              type="button"
              onClick={() => { onActiveViewChange(v); setSelectedCode(null); }}
              className={`rounded-full px-3 py-1 text-[11.5px] font-medium transition-colors ${activeView === v ? 'bg-text text-white' : 'bg-black/[0.05] text-muted hover:bg-black/[0.08]'}`}
            >
              {VIEW_LABELS[v]}
              {count > 0 && <span className="ml-1 opacity-65">· {count}</span>}
              {count > 0 && !hasImage && <span className="ml-1 text-warning" title="Zones on this view have no image">!</span>}
            </button>
          );
        })}
      </div>

      {/* ---- fail-loud guard: a zone whose view has no image cannot proof ---- */}
      {missing.length > 0 && (
        <p className="rounded-[8px] border border-red-300 bg-red-50 px-3 py-2 text-[11px] text-red-700">
          {missing.map((m) => `${m.code} → ${VIEW_LABELS[m.view]}`).join(', ')}
          {missing.length === 1 ? ' has no mockup image' : ' have no mockup image'} — upload one on that tab.
          The proof would composite onto nothing, so saving is blocked.
        </p>
      )}

      {/* ---- fail-loud guard: the image NAME is set but the image does not
           load. zonesMissingImage cannot see this — it only asks whether a
           filename string exists — so without this the operator could save a
           product whose proof would composite onto nothing. ---- */}
      {broken.length > 0 && (
        <p className="rounded-[8px] border border-red-300 bg-red-50 px-3 py-2 text-[11px] text-red-700">
          {broken.map((m) => `${m.code} → ${VIEW_LABELS[m.view]}`).join(', ')}
          {broken.length === 1 ? ' points at a mockup image that failed to load' : ' point at mockup images that failed to load'}
          {' '}— retry it on that tab, or replace it with a file that loads. Saving is blocked:
          an image the console cannot fetch is one the proof renderer cannot fetch either.
        </p>
      )}

      {/* ---- fail-loud guard: two zones that CAN print together, on top of
           each other. Conflicting pairs are exempt — see overlappingZones. ---- */}
      {overlaps.length > 0 && (
        <p className="rounded-[8px] border border-red-300 bg-red-50 px-3 py-2 text-[11px] text-red-700">
          {overlaps.map((o) => `${o.a} + ${o.b} (${VIEW_LABELS[o.view]})`).join(', ')}
          {overlaps.length === 1 ? ' overlaps' : ' overlap'} and can be ordered on the same garment —
          they would print on top of each other. Move or resize one of each pair. Saving is blocked.
        </p>
      )}

      {unplaced.length > 0 && (
        <div className="rounded-[8px] border border-amber-300/70 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
          Not placed on any view: {unplaced.map((code) => (
            <button
              key={code}
              type="button"
              className={`mx-1 underline ${nat ? '' : 'opacity-60'}`}
              title={nat ? undefined : `${VIEW_LABELS[activeView]} has no loaded image to measure against yet.`}
              onClick={() => placeUnplaced(code)}
            >
              {code} → place on {VIEW_LABELS[activeView]}
            </button>
          ))}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-[1fr_320px]">
        {/* ---- canvas + per-view image controls ---- */}
        <div className="min-w-0">
          {baseImage ? (
            <PrizeZoneCanvas
              baseImage={baseImage}
              overlayImage={view?.overlayImage}
              allCodes={allCodes}
              areas={printAreas}
              codes={codes}
              selectedCode={selectedCode}
              onSelect={setSelectedCode}
              onChangeBox={(code, box: Box) => setAreas(setZoneBox(printAreas, code, box))}
              onNatural={(n) => setNats((p) => (
                p[activeView]?.w === n.w && p[activeView]?.h === n.h ? p : { ...p, [activeView]: n }
              ))}
              onLoadState={(s) => noteImageState(activeView, s)}
            />
          ) : (
            <div className="flex h-[260px] items-center justify-center rounded-[10px] border border-dashed border-border px-4 text-center text-[12px] text-muted">
              No {VIEW_LABELS[activeView]} image yet — upload one below, then drop zones onto it.
            </div>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <label className={`cursor-pointer rounded-[8px] border border-black/[0.08] bg-surface px-2 py-1 text-[11px] hover:bg-bg ${uploading ? 'opacity-60' : ''}`}>
              <Upload className="mr-1 inline h-3 w-3" aria-hidden />
              {uploading ? 'Uploading…' : baseImage ? 'Replace image…' : `Upload ${VIEW_LABELS[activeView]} image…`}
              <input
                type="file" accept="image/png,image/jpeg,image/webp" hidden disabled={uploading}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadForView(f); e.currentTarget.value = ''; }}
              />
            </label>
            <select
              className={inputCls}
              value={baseImage}
              onChange={(e) => {
                if (!e.target.value) { removeViewImage(); return; }
                forgetNat(activeView);
                setViews(setViewImage(mockupViews, activeView, { baseImage: e.target.value }));
              }}
            >
              <option value="">— no image —</option>
              {mockupFiles.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <select
              className={inputCls}
              value={view?.overlayImage || ''}
              disabled={!baseImage}
              onChange={(e) => setViews(setViewImage(mockupViews, activeView, { overlayImage: e.target.value }))}
            >
              <option value="">— no overlay —</option>
              {mockupFiles.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            {baseImage && (
              <button
                type="button"
                onClick={removeViewImage}
                title={blockingRemoval.length ? `Blocked — ${blockingRemoval.join(', ')} are placed on this view.` : 'Clear this view image'}
                className={`rounded-[8px] border border-black/[0.08] px-2 py-1 text-[11px] ${blockingRemoval.length ? 'cursor-not-allowed text-subtle opacity-60' : 'text-red-600 hover:bg-bg'}`}
              >
                Remove image
              </button>
            )}
          </div>
          <p className="mt-1 text-[10px] text-muted">
            PNG/JPG/WebP, ≥500px, ≤8 MB. The file name is generated by the server from the product ID and view.
            {nat && ` Base image is ${nat.w}×${nat.h}px — zone coordinates are in those pixels.`}
          </p>
        </div>

        {/* ---- zones on this view ---- */}
        <div>
          <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">
            {VIEW_LABELS[activeView]} zones
          </h4>
          {codes.length === 0 && (
            <p className="rounded-[10px] border border-dashed border-border p-2.5 text-[11.5px] text-muted">
              No zones on this view yet.
            </p>
          )}
          <div className="space-y-2">
            {codes.map((code) => {
              const area = printAreas[code];
              const loc = registry?.locations[code];
              const color = zoneColor(allCodes, code);
              const text = loc ? isTextCapable(loc) : false;
              const note = conflictNote(registry, code);
              return (
                <div
                  key={code}
                  onClick={() => setSelectedCode(code)}
                  className={`rounded-[10px] border p-2.5 transition-colors ${selectedCode === code ? 'border-primary/50 bg-primary/[0.04]' : 'border-black/[0.07]'}`}
                >
                  <div className="mb-1.5 flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} />
                    <span className="text-[12px] font-semibold">{code}</span>
                    <span className="truncate text-[11px] text-muted">
                      {loc ? loc.label : 'unknown location'}{loc?.standard === true ? ' · standard' : ''}
                    </span>
                    <button
                      type="button"
                      className="ml-auto text-subtle transition-colors hover:text-danger"
                      aria-label={`Remove the ${code} zone`}
                      title={`Remove ${code}`}
                      onClick={(e) => { e.stopPropagation(); removeWithUndo(code); }}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                  {!loc && (
                    <p className="mb-1.5 text-[10.5px] text-red-600">
                      "{code}" is not in the decoration registry — remove it or seed the location.
                    </p>
                  )}
                  {/* Informational, never blocking: offering both sides of a
                      conflict is correct — the exclusion happens per order. */}
                  {note && (
                    <p className="mb-1.5 text-[10.5px] text-muted">
                      {code} · {note}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-1.5">
                    <label>
                      <span className={labelCls}>Print W (in)</span>
                      <input
                        className={inputCls} type="number" step="0.1" min="0" value={area.widthInches ?? ''}
                        onChange={(e) => setAreas(setZoneInches(printAreas, code, { widthInches: num(e.target.value) }))}
                      />
                    </label>
                    <label>
                      <span className={labelCls}>Print H (in)</span>
                      <input
                        className={inputCls} type="number" step="0.1" min="0" value={area.heightInches ?? ''}
                        onChange={(e) => setAreas(setZoneInches(printAreas, code, { heightInches: num(e.target.value) }))}
                      />
                    </label>
                  </div>
                  {text && (
                    <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                      <label>
                        <span className={labelCls}>Font</span>
                        <select
                          className={inputCls}
                          value={resolvePrizeFont(area.font)}
                          onChange={(e) => setAreas(setZoneField(printAreas, code, 'font', e.target.value))}
                        >
                          {PRIZE_FONTS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                        </select>
                      </label>
                      <label>
                        <span className={labelCls}>Ink</span>
                        <span className="flex items-center gap-1">
                          <input
                            className="h-[30px] w-[30px] shrink-0 cursor-pointer rounded-[6px] border border-black/[0.08] bg-surface p-0"
                            type="color"
                            aria-label={`${code} ink color`}
                            value={resolvePrizeInk(area.ink).toLowerCase()}
                            onChange={(e) => setAreas(setZoneField(printAreas, code, 'ink', e.target.value.toUpperCase()))}
                          />
                          <input
                            className={inputCls} value={area.ink || ''} placeholder={DEFAULT_PRIZE_INK}
                            onChange={(e) => setAreas(setZoneField(printAreas, code, 'ink', e.target.value))}
                          />
                        </span>
                      </label>
                      {loc?.kinds.includes('name') && (
                        <label>
                          <span className={labelCls}>Max chars</span>
                          <input
                            className={inputCls} type="number" min="1" value={area.maxChars ?? ''}
                            onChange={(e) => setAreas(setZoneField(printAreas, code, 'maxChars', num(e.target.value)))}
                          />
                        </label>
                      )}
                      {loc?.kinds.includes('number') && (
                        <label>
                          <span className={labelCls}>Max digits</span>
                          <input
                            className={inputCls} type="number" min="1" value={area.maxDigits ?? ''}
                            onChange={(e) => setAreas(setZoneField(printAreas, code, 'maxDigits', num(e.target.value)))}
                          />
                        </label>
                      )}
                    </div>
                  )}
                  {/* Exact placement without a mouse. Dragging is a third of an
                      answer: it is pointer-only and imprecise even with one. */}
                  {area.mockup ? (
                    <fieldset className="mt-1.5 border-0 p-0">
                      <legend className={labelCls}>Box on image (px)</legend>
                      <div className="mt-0.5 grid grid-cols-4 gap-1">
                        {([
                          ['x', 'X'], ['y', 'Y'], ['maxW', 'W'], ['maxH', 'H'],
                        ] as [keyof Box, string][]).map(([key, short]) => (
                          <label key={key}>
                            <span className={labelCls}>{short}</span>
                            <input
                              className={inputCls}
                              type="number"
                              step="1"
                              min="0"
                              aria-label={`${code} box ${key}, in image pixels`}
                              value={area.mockup?.[key] ?? ''}
                              onChange={(e) => setBoxField(code, key, e.target.value)}
                            />
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  ) : (
                    <p className="mt-1.5 font-mono text-[10px] text-subtle">no box</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Per-zone undo. Cancel is the only other way back and it discards
              every unrelated edit in the dialog too. */}
          {lastRemoved && (
            <div className="mt-2 flex items-center gap-2 rounded-[8px] border border-black/[0.08] bg-surface px-2.5 py-1.5 text-[11px]">
              <span className="text-muted">
                Removed <span className="font-semibold">{lastRemoved.code}</span>.
              </span>
              <button
                type="button"
                className="font-medium text-primary underline-offset-4 hover:underline"
                onClick={undoRemove}
              >
                Undo
              </button>
              <button
                type="button"
                className="ml-auto text-subtle hover:text-muted"
                aria-label="Dismiss the undo notice"
                onClick={() => setLastRemoved(null)}
              >
                ✕
              </button>
            </div>
          )}

          <AddZoneForm
            key={`${activeView}:${addable.map((l) => l.code).join(',')}`}
            addable={addable}
            registry={registry}
            defaultInches={defaultInches}
            disabledReason={zoneAddBlocked}
            onAdd={(zone) => {
              try {
                // addZone throws rather than guessing when `nat` is null. The
                // form is already gated on it; this is the backstop that makes
                // "guessed geometry got saved" unreachable, not just unlikely.
                setAreas(addZone(printAreas, { ...zone, view: activeView }, nat));
                setSelectedCode(zone.code);
              } catch (e) {
                toast.error(e instanceof Error ? e.message : 'Could not add that zone.');
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}

// Registry-driven zone creation: standard codes are listed first in their own
// group, secondary codes below. Text fields appear only for locations whose
// registry `kinds` allow a name or a number.
function AddZoneForm({ addable, registry, defaultInches, disabledReason, onAdd }: {
  addable: ReturnType<typeof addableLocations>;
  registry?: DecorationRegistry;
  defaultInches: { width: number; height: number };
  disabledReason: string | null;
  onAdd: (zone: { code: string; widthInches: number; heightInches: number; font?: string; maxChars?: number; maxDigits?: number; ink?: string }) => void;
}) {
  const [code, setCode] = useState(addable[0]?.code || '');
  const [w, setW] = useState(String(defaultInches.width));
  const [h, setH] = useState(String(defaultInches.height));
  const [font, setFont] = useState(DEFAULT_PRIZE_FONT);
  const [maxChars, setMaxChars] = useState('');
  const [maxDigits, setMaxDigits] = useState('');
  const [ink, setInk] = useState(DEFAULT_PRIZE_INK);

  const loc = registry?.locations[code];
  const text = loc ? isTextCapable(loc) : false;
  const standard = addable.filter((l) => l.standard === true);
  const other = addable.filter((l) => l.standard !== true);
  const widthIn = num(w);
  const heightIn = num(h);
  const ready = !disabledReason && !!code && !!widthIn && !!heightIn && widthIn > 0 && heightIn > 0;

  if (disabledReason) {
    return <p className="mt-2 rounded-[10px] border border-dashed border-border p-2.5 text-[11px] text-muted">{disabledReason}</p>;
  }

  return (
    <div className="mt-2 rounded-[10px] border border-black/[0.07] p-2.5">
      <h5 className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Add zone</h5>
      <select className={inputCls} value={code} onChange={(e) => setCode(e.target.value)}>
        {standard.length > 0 && (
          <optgroup label="Standard zones">
            {standard.map((l) => <option key={l.code} value={l.code}>{locationOptionLabel(l)}</option>)}
          </optgroup>
        )}
        {other.length > 0 && (
          <optgroup label="Other zones">
            {other.map((l) => <option key={l.code} value={l.code}>{locationOptionLabel(l)}</option>)}
          </optgroup>
        )}
      </select>
      <div className="mt-1.5 grid grid-cols-2 gap-1.5">
        <label>
          <span className={labelCls}>Print W (in)</span>
          <input className={inputCls} type="number" step="0.1" min="0" value={w} onChange={(e) => setW(e.target.value)} />
        </label>
        <label>
          <span className={labelCls}>Print H (in)</span>
          <input className={inputCls} type="number" step="0.1" min="0" value={h} onChange={(e) => setH(e.target.value)} />
        </label>
      </div>
      {text && (
        <div className="mt-1.5 grid grid-cols-2 gap-1.5">
          <label>
            <span className={labelCls}>Font</span>
            <select className={inputCls} value={resolvePrizeFont(font)} onChange={(e) => setFont(resolvePrizeFont(e.target.value))}>
              {PRIZE_FONTS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </label>
          <label>
            <span className={labelCls}>Ink</span>
            <span className="flex items-center gap-1">
              <input
                className="h-[30px] w-[30px] shrink-0 cursor-pointer rounded-[6px] border border-black/[0.08] bg-surface p-0"
                type="color"
                aria-label="New zone ink color"
                value={resolvePrizeInk(ink).toLowerCase()}
                onChange={(e) => setInk(e.target.value.toUpperCase())}
              />
              <input className={inputCls} value={ink} placeholder={DEFAULT_PRIZE_INK} onChange={(e) => setInk(e.target.value)} />
            </span>
          </label>
          {loc?.kinds.includes('name') && (
            <label>
              <span className={labelCls}>Max chars</span>
              <input className={inputCls} type="number" min="1" value={maxChars} onChange={(e) => setMaxChars(e.target.value)} />
            </label>
          )}
          {loc?.kinds.includes('number') && (
            <label>
              <span className={labelCls}>Max digits</span>
              <input className={inputCls} type="number" min="1" value={maxDigits} onChange={(e) => setMaxDigits(e.target.value)} />
            </label>
          )}
        </div>
      )}
      <Button
        size="sm" variant="secondary" className="mt-2" disabled={!ready}
        onClick={() => onAdd({
          code,
          widthInches: widthIn as number,
          heightInches: heightIn as number,
          ...(text && isPrizeFont(font) ? { font } : {}),
          ...(text && ink ? { ink } : {}),
          ...(text && num(maxChars) ? { maxChars: num(maxChars) } : {}),
          ...(text && num(maxDigits) ? { maxDigits: num(maxDigits) } : {}),
        })}
      >
        <Plus className="h-3.5 w-3.5" aria-hidden /> Add zone
      </Button>
    </div>
  );
}
